/**
 * Everything a viewer shows about a build, in one call.
 *
 * The sheet, the per-stat breakdowns, the damage figure, the rotation, the defence figure and
 * the diagnostics — what the planner's panels read, and what the build catalogue, its indexer
 * and the Discord bot read too. It lives in the engine rather than in the app so that every one
 * of those computes the same numbers from the same document: a second copy of this module is a
 * second place for the catalogue to disagree with the planner a player clicks through to.
 *
 * Measured against the real 2.0.2 snapshot on the level-100 capture, everything below — the stat
 * sheet, the damage figure, the rotation and the defence figure — is ~7 ms for a document the
 * engine has not seen before, and a third of a millisecond for a second ask about the same one.
 *
 * Most of that is the exile-effect fixed point: what is up depends on the sheet (charge caps,
 * `give_exile_effect` grants, buff strengths) and the sheet depends on what is up, so `calculate`
 * runs a pass with nothing applied and then iterates until two answers agree. `resolveEffects`
 * memoises the settled answer against the document object, which is why the four calls here cost
 * one loop between them rather than four. An edit produces a new document and pays it again.
 */
import { isLegal, validateBuild } from "@cte2/schema";
import { calculate } from "./calculate.js";
import { basicAttack } from "./damage/basic-attack.js";
import { defence } from "./damage/defence.js";
import { simulateDps, simulateFullDps } from "./damage/dps.js";
import { resources } from "./damage/resources.js";
import { selfSustain } from "./damage/self-sustain.js";
import { statIndex } from "./stat-def.js";
/**
 * Turns an engine result into a `breakdown(statId)`.
 *
 * Shared by the character sheet and the spell unit, which is what keeps the two drill-downs
 * honest about each other: the same rollup, the same reconstruction of the container's three
 * running totals, the same handling of transfers. Anything that only made sense for one of them
 * would be a place the two could quietly disagree.
 *
 * The index is built once per result rather than per stat, because a geared character carries a
 * few thousand modifiers and the panel would otherwise rescan all of them every time a row is
 * opened.
 */
export function breakdownsOf(result, snapshot) {
    const index = statIndex(snapshot);
    const byStat = new Map();
    for (const ctx of result.contexts) {
        for (const mod of ctx.stats) {
            let list = byStat.get(mod.statId);
            if (!list) {
                list = [];
                byStat.set(mod.statId, list);
            }
            list.push({
                ctxType: ctx.type,
                source: ctx.source,
                path: ctx.path,
                type: mod.type,
                value: mod.value,
                ...(mod.from === undefined ? {} : { from: mod.from }),
            });
        }
    }
    const derivedByStat = new Map();
    // Transfers are also indexed by their *source*, so a stat can explain why it reads 0.
    const transferredTo = new Map();
    for (const entry of result.derived) {
        let list = derivedByStat.get(entry.statId);
        if (!list) {
            list = [];
            derivedByStat.set(entry.statId, list);
        }
        list.push(entry);
        if (entry.kind === "transfer") {
            let targets = transferredTo.get(entry.from);
            if (!targets) {
                targets = [];
                transferredTo.set(entry.from, targets);
            }
            if (!targets.includes(entry.statId))
                targets.push(entry.statId);
        }
    }
    return (statId) => {
        const contributions = byStat.get(statId) ?? [];
        const derivedHere = derivedByStat.get(statId) ?? [];
        let flat = 0;
        let percent = 0;
        let multi = 1;
        let addedAfterCalc = 0;
        for (const c of contributions) {
            if (c.type === "FLAT")
                flat += c.value;
            else if (c.type === "PERCENT")
                percent += c.value;
            else
                multi *= 1 + c.value / 100;
        }
        // Transfers and the core-stat pass both re-enter the container, so they belong in these
        // totals; the after-calc passes do not, because they write past it.
        for (const d of derivedHere) {
            if (d.type === "FLAT")
                flat += d.value;
            else if (d.type === "PERCENT")
                percent += d.value;
            else if (d.type === "MORE")
                multi *= 1 + d.value / 100;
            // `addFullyTo` adds to the multiplier rather than multiplying by it — reproduced in
            // the engine, so reproduced here.
            else if (d.type === "MULTI_ADD")
                multi += d.value;
            else
                addedAfterCalc += d.value;
        }
        return {
            statId,
            stat: result.stats.get(statId) ?? { value: 0, dmgMulti: 1, hardcap: 0, softcap: 0 },
            contributions,
            derived: derivedHere,
            flat,
            percent,
            multi,
            base: index.shapeOf(statId).base,
            addedAfterCalc,
            transferredTo: transferredTo.get(statId) ?? [],
        };
    };
}
/**
 * Validate, calculate and simulate one document — the whole derived view of it.
 *
 * Never throws for a document mid-edit: a damage pipeline that fails is reported as a
 * diagnostic and leaves the damage fields undefined, because a viewer that loses one panel beats
 * one that loses the page.
 */
export function deriveBuild(doc, snapshot) {
    const started = performance.now();
    const validation = validateBuild(doc, snapshot);
    const result = calculate(doc, snapshot);
    // `simulateHit` throws nothing for a legal-but-odd build, but a document mid-edit can name a
    // spell that does not exist yet. A damage panel that disappears beats one that takes the app
    // down, and the validator already says why.
    let damage;
    let dps;
    let fullDps;
    let damageDiagnostics = [];
    // With the swing as the main figure there is no main Skill to ask about (`simulateDps` returns
    // nothing), but a rotation can still be ticked beside it.
    if ((doc.skills ?? []).length > 0) {
        try {
            // `breakdown` is on because the Damage tab always shows the trace; it is one array push
            // per layer write, which is nothing next to the two stat calculations underneath it.
            // Placement, pack size and any coverage overrides are read from the document by
            // `simulateDps` itself, so there is nothing to thread through here.
            dps = simulateDps(doc, snapshot, { breakdown: true });
            damage = dps?.hit;
            damageDiagnostics = dps?.diagnostics ?? [];
            // Cheap when nothing is ticked, and it re-runs `simulateDps` per ticked skill otherwise.
            // No breakdown: the rotation view shows totals, and the trace belongs to the single skill.
            fullDps = simulateFullDps(doc, snapshot);
            damageDiagnostics = [...damageDiagnostics, ...fullDps.diagnostics];
        }
        catch (err) {
            damageDiagnostics = [
                {
                    severity: "error",
                    code: "damage-pipeline-threw",
                    path: "skills",
                    message: `The damage pipeline failed: ${err instanceof Error ? err.message : String(err)}`,
                },
            ];
        }
    }
    // The swing, handed the sheet that is already in hand so it pays for the layer sweep alone and
    // not for a second exile-effect fixed point. It throws on the same half-built documents the
    // damage pipeline does — a weapon whose base no longer exists — and a missing swing is a figure
    // reading 0, not a panel that disappears.
    //
    // The main skill and the rotation go with it, because a swing proc that spends a debuff —
    // Cryogenic Rupture taking a Snow-Tracked stack — fires no faster than those put it back, and
    // they are already computed. With the swing as the main figure it also carries a trace, since
    // the Damage tab then breaks the swing down instead of a Skill.
    let basic;
    try {
        basic = basicAttack(doc, snapshot, {
            sheets: { character: result, spell: result },
            supply: {
                ...(dps === undefined ? {} : { main: dps }),
                ...(fullDps === undefined || fullDps.skills.length === 0 ? {} : { rotation: fullDps }),
            },
            ...(doc.config?.mainIsBasicAttack === true ? { breakdown: true } : {}),
        });
    }
    catch {
        basic = undefined;
    }
    const diagnostics = dedupe([...validation, ...result.diagnostics, ...damageDiagnostics]);
    const elapsedMs = performance.now() - started;
    // One index per sheet, built once rather than per stat — see `breakdownsOf`. The spell unit is
    // indexed only when there is one, so a build with no skill pays for nothing.
    const characterBreakdown = breakdownsOf(result, snapshot);
    const spellBreakdown = damage === undefined ? undefined : breakdownsOf(damage.sheets.spell, snapshot);
    // Handed the sheet that was already computed, so this is the layer sweep and nothing else.
    const pools = resources(doc, snapshot, { sheet: result });
    return {
        doc,
        stats: result.stats,
        effects: dps?.effects ?? result.effects,
        defence: defence(doc, snapshot, { sheet: result }),
        resources: pools,
        selfSustain: sustainOf(dps, pools),
        contexts: result.contexts,
        derived: result.derived,
        diagnostics,
        legal: isLegal(validation),
        damage,
        dps,
        fullDps,
        basic,
        elapsedMs,
        breakdown: characterBreakdown,
        skillBreakdown: (statId) => spellBreakdown?.(statId),
    };
}
/**
 * The self-damage sustain question, asked of the pools the resource pass just reported.
 *
 * The in-combat column, deliberately: this is a drain you take while fighting, and setting it
 * against the standing-still regeneration would flatter every build on a server that runs the
 * mod's own 0.5 multiplier rather than this pack's 1.0.
 */
function sustainOf(dps, pools) {
    const self = dps?.selfDamage;
    if (self === undefined)
        return undefined;
    const of = (id) => {
        const row = pools.byResource.find((r) => r.resource === id);
        return { max: row?.max ?? 0, perSecond: row?.inCombatPerSecond ?? 0 };
    };
    return selfSustain({
        perSecond: self.perSecond,
        perCast: self.perCast,
        magicShield: of("magic_shield"),
        health: of("health"),
        cutoffShare: self.cutoffShare,
    });
}
/**
 * Identical findings arrive from more than one source — the validator and the engine both
 * notice an unknown stat id, and `simulateHit` runs `calculate` twice internally.
 */
function dedupe(diagnostics) {
    const seen = new Set();
    const out = [];
    for (const d of diagnostics) {
        const key = `${d.severity}\0${d.code}\0${d.path}\0${d.message}`;
        if (seen.has(key))
            continue;
        seen.add(key);
        out.push(d);
    }
    return out;
}
/**
 * Which skill the damage figures are about, as an index into `doc.skills`.
 *
 * Read back off the run rather than re-derived, because the rule is not "the first skill" and a
 * second copy of it silently disagrees. `simulateDps` takes the one marked `main`; failing that
 * **the first enabled skill that declares a damage act**, because a capture whose bar starts
 * with Protection would otherwise report 0 DPS for a build carrying Quake — and every what-if
 * in the planner is a difference between two DPS numbers, so that zero propagated into the tree
 * hover, the gem ranking and the Damage tab all reading "nothing changes".
 *
 * Two panels defaulted their skill selector to `findIndex(s => s.main)` and fell back to 0, and
 * on exactly that document they opened on Protection while the sidebar beside them reported
 * Quake's numbers.
 *
 * Falls back to 0 for a document with no damage figure at all, which is the only index that
 * exists for it.
 */
export function mainSkillIndex(derived) {
    const skills = derived.doc.skills ?? [];
    const spellId = derived.dps?.spellId;
    if (spellId === undefined)
        return 0;
    const at = skills.findIndex((skill) => skill.spellId === spellId);
    return at < 0 ? 0 : at;
}
//# sourceMappingURL=derive.js.map